"use client";

import { useContext, useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import logo from "../assets/3D-Medico Logo mit Text groß.png";
import { LoginContext } from "../LoginContext";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { apiService } from "../backend/api";
import DarkModeToggle from "./DarkModeToggle";
import Logo3D from "./Logo3D";
import { getBaseUrl } from "../config/apiConfig";
import {
  User,
  CreditCard,
  Cloud,
  Lock,
  MessageSquare,
  Settings,
  LogOut,
  LogIn,
  Menu,
  X,
  ChevronDown,
  LayoutDashboard,
  Upload,
  Box,
  Home,
  MessagesSquare,
  ScanLine,
  FolderOpen,
  Tag,
  Bone
} from "lucide-react";

const Header = () => {
  const { loginInfo, logout } = useContext(LoginContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const dropdownRef = useRef(null);
  const mobileProfileRef = useRef(null);
  const { t } = useTranslation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    apiService
      .logoutUser(loginInfo.username)
      .then(() => {
        logout();
        navigate("/login");
      })
      .catch((error) => {
        console.error("Logout error:", error);
        logout();
        navigate("/login");
      });
  };

  const isLiveMode = process.env.REACT_APP_APP_MODE === 'live';

  const navItems = [
    { label: t("Home"), path: "/", icon: Home },
    { label: t("Video-3D-Scan"), path: "/upload", icon: ScanLine },
    // { label: t("Scan (Demo)"), path: "/WSUploadDemo", icon: Box },
    { label: t("Scanbearbeitung"), path: "/modelviewer", icon: Box },
    ...(!isLiveMode ? [{ label: t("Orthesen-Editor"), path: "/orthesis-editor", icon: Bone }] : []),
    { label: t("Meine Projekte"), path: "/projects", icon: FolderOpen },
    { label: t("Preise"), path: "/pakete", icon: Tag },
  ];

  const profileSubpages = [
    { label: t("Profile Details"), path: "/profile/details", icon: User },
    { label: t("Subscription Details"), path: "/profile/subscription", icon: CreditCard },
    { label: t("Healthcloud"), path: "/profile/healthcloud", icon: Cloud },
    { label: t("Change Password"), path: "/profile/change-password", icon: Lock },
    { label: t("Messages"), path: "/profile/messages", icon: MessageSquare },
    { label: t("Accessibility"), path: "/profile/accessibility", icon: Settings },
  ];

  useEffect(() => {
    const fetchAdminStatus = async () => {
      // Check from loginInfo first (restored by LoginContext)
      const userRoles = loginInfo?.user?.roles || [];
      const hasAdminRole = userRoles.some(r =>
        (typeof r === 'string' && r === 'admin') ||
        (r && typeof r === 'object' && r.name === 'admin')
      );

      if (hasAdminRole) {
        setIsAdmin(true);
        // We still continue to fetch to "refresh" the status from the server
      }

      if (!loginInfo?.username) return;

      try {
        const roles = await apiService.request(`${getBaseUrl()}/api/user/userRoles`);

        if (roles && Array.isArray(roles)) {
          const hasAdmin = roles.some((r) =>
            (typeof r === 'string' && r === 'admin') ||
            (r && typeof r === 'object' && r.name === 'admin')
          );
          setIsAdmin(hasAdmin);
        }
      } catch (err) {
        console.error("Error checking admin status:", err);
        setIsAdmin(prev => prev || hasAdminRole);
      }
    };

    fetchAdminStatus();
  }, [loginInfo?.username]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (mobileProfileRef.current && !mobileProfileRef.current.contains(event.target)) {
        setMobileProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 h-20 bg-gradient-to-r from-[#0f7699] to-[#1e4e5f] text-white shadow-[0_4px_20px_rgba(0,0,0,0.15)] z-[2000] px-4 sm:px-6 py-2 transition-all duration-300">
      <div className="max-w-[1400px] h-full mx-auto flex items-center justify-between">

        {/* Logo Section */}
        <div
          className="flex items-center gap-4 cursor-pointer hover:opacity-90 transition-all group shrink-0"
          onClick={() => navigate("/")}
        >
          <div className="relative">
            <Logo3D size={45} />
            <div className="absolute -inset-1 bg-white/20 rounded-full blur-sm group-hover:blur-md transition-all opacity-0 group-hover:opacity-100" />
          </div>
          <div className="flex flex-col">
            <h1 className="text-xl font-black tracking-tight leading-none text-white drop-shadow-sm">
              {t("3D-Medico")}
            </h1>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 mt-1 hidden lg:block">
              {t("Pioneering Healthcare")}
            </p>
          </div>
        </div>

        {/* Mobile: Language + Chat buttons next to logo (only when logged in) */}
        {loginInfo && (
          <div className="sm:hidden flex items-center gap-2">
            <LanguageSwitcher variant="mobile-compact" />
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('toggle-help-chat'))}
              className="w-10 h-10 flex items-center justify-center bg-white/15 rounded-full text-white hover:bg-white/25 transition-all active:scale-95 relative"
              title={t("Support Chat")}
            >
              <MessagesSquare size={20} />
            </button>
          </div>
        )}

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-2 lg:gap-4 flex-1 justify-center max-w-3xl mx-auto px-4">
          {loginInfo && navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`
                px-4 py-2.5 rounded-xl text-sm font-bold transition-all relative group whitespace-nowrap text-center
                ${location.pathname === item.path
                  ? "text-white bg-white/15"
                  : "text-white/80 hover:text-white hover:bg-white/10"}
              `}
            >
              {item.label}
              {location.pathname === item.path && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-white rounded-full animate-in zoom-in duration-300" />
              )}
            </Link>
          ))}
          {loginInfo && isAdmin && (
            <Link
              to="/admin/dashboard"
              className={`
                px-4 py-2 rounded-xl text-sm font-black transition-all relative border border-[#fbbf24]/30 text-[#fbbf24] hover:bg-[#fbbf24]/10 whitespace-nowrap
                ${location.pathname === "/admin/dashboard" ? "bg-[#fbbf24]/20 border-[#fbbf24]" : ""}
              `}
            >
              {t("Dashboard")}
            </Link>
          )}
        </div>

        {/* Right Section Tools */}
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 bg-black/10 rounded-2xl px-2 py-1 border border-white/5">
            <DarkModeToggle />
            <div className="w-[1px] h-4 bg-white/20" />
            <LanguageSwitcher />
          </div>

          {loginInfo ? (
            <div className="relative hidden md:block" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`
                  w-11 h-11 flex items-center justify-center rounded-2xl transition-all duration-300 border-2
                  ${dropdownOpen
                    ? "bg-white text-[#0f7699] border-white shadow-lg"
                    : "bg-white/10 text-white border-transparent hover:bg-white/20 hover:scale-105"}
                `}
              >
                <User size={24} />
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-4 w-72 bg-white rounded-[2rem] shadow-2xl py-4 z-[2005] border border-slate-100 animate-in fade-in slide-in-from-top-4 duration-300 overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-50 bg-slate-50/50">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t("Eingeloggt als")}</p>
                    <p className="text-slate-900 font-black truncate">{loginInfo.username}</p>
                  </div>

                  <div className="py-2">
                    {profileSubpages.map((page) => (
                      <button
                        key={page.path}
                        onClick={() => {
                          navigate(page.path);
                          setDropdownOpen(false);
                        }}
                        className="w-full flex items-center gap-4 px-6 py-3.5 text-slate-600 hover:text-[#0f7699] hover:bg-slate-50 transition-all font-black text-sm text-left group"
                      >
                        <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center transition-colors group-hover:bg-[#0f7699]/10">
                          <page.icon size={18} />
                        </div>
                        <span>{page.label}</span>
                      </button>
                    ))}
                  </div>

                  <div className="px-4 mt-2">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-4 px-4 py-4 rounded-[1.25rem] text-rose-500 hover:bg-rose-50 transition-all font-black text-sm group"
                    >
                      <div className="w-8 h-8 rounded-xl bg-rose-100 flex items-center justify-center group-hover:bg-rose-500 group-hover:text-white transition-all">
                        <LogOut size={18} />
                      </div>
                      <span>{t("Abmelden")}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Desktop: white login button */}
              <Link
                to="/login"
                className="hidden sm:flex px-6 h-11 items-center bg-white text-[#0f7699] rounded-2xl font-black text-sm shadow-lg shadow-black/10 hover:-translate-y-0.5 hover:shadow-xl transition-all active:translate-y-0"
              >
                {t("Login")}
              </Link>
              {/* Mobile: language + round chat button (replaces white login button) */}
              <div className="sm:hidden flex items-center gap-2">
                <LanguageSwitcher variant="mobile-compact" />
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('toggle-help-chat'))}
                  className="w-11 h-11 flex items-center justify-center bg-white/15 rounded-full text-white hover:bg-white/25 transition-all active:scale-95"
                  title={t("Support Chat")}
                >
                  <MessagesSquare size={20} />
                </button>
              </div>
            </>
          )}

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden w-11 h-11 flex items-center justify-center bg-white/10 rounded-2xl text-white hover:bg-white/20 transition-all active:scale-95"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <div className={`
        fixed inset-y-0 right-0 w-[85vw] max-w-[300px] bg-white dark:bg-slate-900 text-slate-900 dark:text-white z-[2500] shadow-2xl transition-transform duration-500 ease-spring
        ${menuOpen ? "translate-x-0" : "translate-x-full"}
      `}>
        <div className="flex flex-col h-full">
          <div className="p-8 flex justify-between items-center border-b border-slate-50 dark:border-slate-800">
            <Logo3D size={40} />
            <button onClick={() => setMenuOpen(false)} className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500">
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto py-8 px-4 space-y-2">
            <p className="px-4 text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-widest mb-4">{t("Navigation")}</p>
            {loginInfo && navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setMenuOpen(false);
                }}
                className={`
                  w-full flex items-center gap-4 px-4 py-4 rounded-2xl font-black text-left transition-all
                  ${location.pathname === item.path
                    ? "bg-[#0f7699] text-white shadow-lg shadow-[#0f7699]/20"
                    : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}
                `}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </button>
            ))}
            {loginInfo && isAdmin && (
              <button
                onClick={() => {
                  navigate("/admin/dashboard");
                  setMenuOpen(false);
                }}
                className={`
                  w-full flex items-center gap-4 px-4 py-4 rounded-2xl font-black text-left border-2 border-[#fbbf24]/20 text-[#fbbf24] mt-4
                  ${location.pathname === "/admin/dashboard" ? "bg-[#fbbf24]/10 border-[#fbbf24]" : ""}
                `}
              >
                <LayoutDashboard size={20} />
                <span>{t("Admin Dashboard")}</span>
              </button>
            )}

            {loginInfo && (
              <>
                <div className="h-[1px] bg-slate-100 dark:bg-slate-800 my-8 mx-4" />
                <p className="px-4 text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-widest mb-4">{t("Profil settings")}</p>
                {profileSubpages.map((page) => (
                  <button
                    key={page.path}
                    onClick={() => {
                      navigate(page.path);
                      setMenuOpen(false);
                    }}
                    className={`
                      w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl font-black text-left transition-all
                      ${location.pathname === page.path ? "text-[#0f7699] dark:text-[#4fc3f7] bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10" : "text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"}
                    `}
                  >
                    <page.icon size={18} />
                    <span>{page.label}</span>
                  </button>
                ))}
              </>
            )}
          </div>

          <div className="p-8 border-t border-slate-100 dark:border-slate-800 space-y-4">
            <div className="flex items-center gap-4 p-2 bg-slate-50 dark:bg-slate-800 rounded-2xl">
              <DarkModeToggle variant="sidebar" />
              <div className="w-[1px] h-4 bg-slate-200 dark:bg-slate-700" />
              <LanguageSwitcher variant="sidebar" />
            </div>
            {loginInfo ? (
              <button
                onClick={handleLogout}
                className="w-full h-14 rounded-2xl bg-rose-50 dark:bg-rose-900/20 text-rose-500 dark:text-rose-400 font-black flex items-center justify-center gap-3 active:scale-95 transition-all"
              >
                <LogOut size={20} />
                <span>{t("Abmelden")}</span>
              </button>
            ) : (
              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
                className="w-full h-14 rounded-2xl bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 font-black flex items-center justify-center gap-3 active:scale-95 transition-all shadow-lg shadow-[#0f7699]/20"
              >
                <LogIn size={20} />
                <span>{t("Anmelden")}</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Mobile floating login button - bottom right (only when not logged in) */}
      {!loginInfo && (
        <div className="fixed bottom-8 right-8 z-[9998] sm:hidden">
          <Link
            to="/login"
            className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0f7699] to-[#4c9fb9] text-white shadow-[0_8px_32px_rgba(15,118,153,0.4)] flex items-center justify-center transition-all duration-500 hover:scale-110 active:scale-95"
          >
            <LogIn size={28} />
          </Link>
        </div>
      )}
    </header>
  );
};

export default Header;
